import { RouteInfo } from "./sidebar.metadata";
import { Role } from "../../../models/role";
import {environment} from "../../../../../environments/environment";

const P = environment.PERMISO;
const C = Role;
const RRHH = [C.CARGO_ADMIN, C.CARGO_SISTEMAS];
const RRHH_Y_JEFE = [C.CARGO_ADMIN, C.CARGO_SISTEMAS, C.CARGO_JEFE];

/**
 * Menu lateral. Tres niveles: modulo -> submenu -> opcion.
 * Una entrada se muestra si el cargo del usuario esta en `role` (o es "All")
 * y tiene alguno de los permisos de `permissionList` (vacio = sin restriccion).
 * Un grupo lleva la union de cargos y permisos de sus hijos, para que aparezca
 * cuando el usuario puede ver al menos uno.
 */

/** Modulo de primer nivel con enlace directo. */
function modulo(title: string, path: string, icon: string, role: string[], permissionList: string[] = [], moduleName = path): RouteInfo {
    return {path, title, moduleName, iconType: "material-icons-two-tone", icon, class: "", groupTitle: false,
        badge: "", badgeClass: "", role, permissionList, submenu: []};
}

/** Modulo de primer nivel que despliega un submenu. */
function grupo(title: string, moduleName: string, icon: string, role: string[], submenu: RouteInfo[]): RouteInfo {
    return {path: "", title, moduleName, iconType: "material-icons-two-tone", icon, class: "menu-toggle", groupTitle: false,
        badge: "", badgeClass: "", role, permissionList: permisosDe(submenu), submenu};
}

/** Opcion de segundo nivel con enlace directo. */
function opcion(title: string, path: string, moduleName: string, permissionList: string[], role: string[] = ["All"]): RouteInfo {
    return {path, title, moduleName, iconType: "", icon: "", class: "ml-menu", groupTitle: false,
        badge: "", badgeClass: "", role, permissionList, submenu: []};
}

/** Opcion de segundo nivel que despliega un tercer nivel. */
function subgrupo(title: string, moduleName: string, role: string[], submenu: RouteInfo[]): RouteInfo {
    return {path: "/", title, moduleName, iconType: "", icon: "", class: "ml-sub-menu", groupTitle: false,
        badge: "", badgeClass: "", role, permissionList: permisosDe(submenu), submenu};
}

/** Opcion de tercer nivel. */
function subopcion(title: string, path: string, permissionList: string[], role: string[] = ["All"]): RouteInfo {
    // moduleName unico: el menu marca como activa la opcion cuyo moduleName coincide con el ultimo clic
    return {path, title, moduleName: path, iconType: "", icon: "", class: "ml-sub-sub-menu", groupTitle: false,
        badge: "", badgeClass: "", role, permissionList, submenu: []};
}

/** Union de los permisos de los hijos. Si alguno no pide permiso, el grupo tampoco. */
function permisosDe(items: RouteInfo[]): string[] {
    if (items.some(i => i.permissionList.length === 0)) {
        return [];
    }
    return Array.from(new Set(items.reduce((todos, i) => todos.concat(i.permissionList), [] as string[])));
}

export const ROUTES: RouteInfo[] = [
    modulo("Dashboard", "dashboard", "dashboard", ["All"]),

    grupo("Solicitudes", "solicitud", "assignment", RRHH_Y_JEFE, [
        opcion("Vacaciones", "/solicitud/vacaciones", "vacaciones", [P.SOLICITUD_VACACIONES], RRHH_Y_JEFE),
        opcion("Movilidad", "/solicitud/movilidad", "movilidad", [P.SOLICITUD_MOVILIDAD], RRHH_Y_JEFE),
        opcion("Cupones", "/solicitud/cupones", "Cupones", [P.SOLICITUD_CUPONES], RRHH_Y_JEFE),
        opcion("Descanso médico", "/solicitud/descansomedico", "DescansoMedico", [P.SOLICITUD_DESCANSO_MEDICO], RRHH_Y_JEFE),
        opcion("Permiso", "/solicitud/permiso", "Permiso", [P.SOLICITUD_PERMISO], RRHH_Y_JEFE),
    ]),

    grupo("RRHH", "rrhh", "account_box", RRHH_Y_JEFE, [
        subgrupo("Gestión", "rrhhgestion", RRHH, [
            subopcion("Trabajadores", "/trabajador/gestion", [P.GEST_TRAB_GESTION]),
            subopcion("Cesados", "/trabajador/cese", [P.GEST_TRAB_CESADOS]),
            subopcion("Importador", "/trabajador/importador", [P.GEST_TRAB_IMPORTADOR]),
        ]),
        opcion("Planilla y Sueldos", "/planilla", "planilla", [P.PLANILLA], RRHH),
        subgrupo("Asistencia", "rrhhasistencia", RRHH_Y_JEFE, [
            subopcion("Reporte General", "/asistencia/reporte", [P.ASISTENCIA_REPORTE_GENERAL], RRHH),
            subopcion("Reporte Jefe", "/asistencia/reportejefe", [P.ASISTENCIA_REPORTE_JEFE], RRHH_Y_JEFE),
            subopcion("Consolidado Horas", "/asistencia/consolidado", [P.ASISTENCIA_CONSOLIDADO_HORAS], RRHH_Y_JEFE),
        ]),
    ]),

    grupo("Campañas", "campanias", "campaign", RRHH_Y_JEFE, [
        opcion("Clima Laboral", "/climalaboral", "climalaboral", [P.CLIMA_LABORAL], RRHH),
        opcion("Desempeño", "/desempeno", "desempeno", [P.DESEMPENO], RRHH_Y_JEFE),
        opcion("Phishing", "/pishing", "pishing", [P.CAMPANAS_PHISHING], RRHH),
    ]),

    grupo("Sistemas", "sistema", "folder_shared", [C.CARGO_SISTEMAS], [
        opcion("Accesos PC", "/sistema/pc", "accesopc", [P.DATA_TRAB_ACCESOS_PC]),
        opcion("Inventario", "/sistema/inventario", "computadora", [P.DATA_TRAB_INVENTARIO]),
        opcion("Accesos Generales", "/sistema/accesos", "accesos", [P.DATA_TRAB_ACCESOS_GENERALES]),
        opcion("Importador de equipos", "/sistema/importador", "importador", [P.DATA_TRAB_IMPORTADOR]),
    ]),

    modulo("Reportes", "reportes", "assessment", RRHH, [P.REPORTES]),

    grupo("Configuración", "configuracion", "settings", RRHH, [
        opcion("Empresas", "/registro/empresa", "empresa", [P.REGISTRO_EMPRESA]),
        opcion("Usuarios", "/usuario", "usuario", [P.GESTION_USUARIOS]),
        opcion("Correos", "/registro/correoalert", "correoalert", [P.REGISTRO_CORREOS]),
        opcion("Sedes", "/registro/sede", "sede", [P.REGISTRO_SEDES]),
        opcion("Áreas", "/registro/area", "area", [P.REGISTRO_AREAS]),
        opcion("Puestos", "/registro/puesto", "puesto", [P.REGISTRO_PUESTOS]),
        opcion("Equipos", "/sistema/registro/equipo", "equipo", [P.DATA_TRAB_REGISTRO_EQUIPOS]),
        opcion("Teléfonos", "/sistema/registro/telefono", "telefono", [P.DATA_TRAB_REGISTRO_TELEFONO]),
        opcion("Validar Fotocheck", "/trabajador/validar", "validar", [P.GEST_TRAB_VALIDAR_FOTOCHECK]),
    ]),

];
